import { apiJson, type ApiFetchInit } from "../../api/client";
import type { MemberLicense } from "../types";

// Grant a seat to a member. accountId + the cap are resolved server-side from
// the caller's session — the body only carries the target user. The backend
// gates this to owner/admin (403 otherwise).
export function grantLicense(
  userId: string | number,
  init: ApiFetchInit = {},
): Promise<MemberLicense> {
  return apiJson<MemberLicense>("/team/licenses/grant", {
    ...init,
    method: "POST",
    headers: { "Content-Type": "application/json", ...init.headers },
    body: JSON.stringify({ userId: Number(userId) }),
  });
}

// Revoke a seat. Idempotent on the server: revoking when there's no active
// row returns { revoked: 0 } (not 404), so re-clicks and races aren't errors.
export function revokeLicense(
  userId: string | number,
  init: ApiFetchInit = {},
): Promise<{ revoked: number }> {
  return apiJson<{ revoked: number }>("/team/licenses/revoke", {
    ...init,
    method: "POST",
    headers: { "Content-Type": "application/json", ...init.headers },
    body: JSON.stringify({ userId: Number(userId) }),
  });
}

// AHA-41755 bulk-action endpoints. Used by the action-bar dispatch so a
// 50-row Assign click is one round-trip instead of fifty. Backend caps the
// payload at 100 userIds — the embed's bar can only ever select what's on
// the current page (PAGE_SIZE = 50), so the cap is purely defensive.

export interface GrantLicenseBulkSkip {
  userId: number;
  /** `already_licensed` — userId already had an active row (own request or
   *  a race with a concurrent grant). `cap_exceeded` — partial-success
   *  case: the cap was hit partway through, remaining userIds skipped FCFS. */
  reason: "already_licensed" | "cap_exceeded";
}

export interface GrantLicenseBulkResponse {
  granted: MemberLicense[];
  skipped: GrantLicenseBulkSkip[];
}

export function grantLicenseBulk(
  userIds: Array<string | number>,
  init: ApiFetchInit = {},
): Promise<GrantLicenseBulkResponse> {
  return apiJson<GrantLicenseBulkResponse>("/team/licenses/grant-bulk", {
    ...init,
    method: "POST",
    headers: { "Content-Type": "application/json", ...init.headers },
    body: JSON.stringify({ userIds: userIds.map((u) => Number(u)) }),
  });
}

export interface RevokeLicenseBulkResponse {
  revoked: number;
  /** The userIds we actually flipped. Lets the caller patch only the rows
   *  that changed instead of refetching the whole list. */
  revokedUserIds: number[];
}

export function revokeLicenseBulk(
  userIds: Array<string | number>,
  init: ApiFetchInit = {},
): Promise<RevokeLicenseBulkResponse> {
  return apiJson<RevokeLicenseBulkResponse>("/team/licenses/revoke-bulk", {
    ...init,
    method: "POST",
    headers: { "Content-Type": "application/json", ...init.headers },
    body: JSON.stringify({ userIds: userIds.map((u) => Number(u)) }),
  });
}
